import { isTeachPwaSurface } from './teachPwaManifest';
import { isTeachPwaSubdomainHost } from './pwaInstallHosts';

const TEACH_PWA_SUBDOMAIN_ORIGIN = 'https://teach.americanpolicetraining.com';

/** Map apex `/teach` paths to `teach.*` host paths (e.g. `/teach/events/foo` → `/events/foo`). */
export function resolveTeachPathForSubdomainHost(pathname: string): string {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (normalized === '/teach') {
    return '/';
  }
  if (normalized.startsWith('/teach/')) {
    const remainder = normalized.slice('/teach'.length);
    return remainder || '/';
  }
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
}

/** Public install/handoff URL on the dedicated `teach.*` PWA origin. */
export function resolveTeachPwaSubdomainUrl(pathname = '/teach', hostname?: string): string {
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const path = resolveTeachPathForSubdomainHost(pathname);
  if (isTeachPwaSubdomainHost(host) && typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }
  if (
    host === 'americanpolicetraining.com' ||
    host === 'www.americanpolicetraining.com' ||
    host === ''
  ) {
    return `${TEACH_PWA_SUBDOMAIN_ORIGIN}${path}`;
  }
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }
  return `${TEACH_PWA_SUBDOMAIN_ORIGIN}${path}`;
}

/** In-app home route for the Teach APT PWA on the current host. */
export function resolveTeachPwaHomePath(pathname?: string, hostname?: string): string {
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/teach');
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  if (isTeachPwaSurface(path, host)) {
    const primaryLabel = host.split('.')[0]?.toLowerCase() ?? '';
    if (primaryLabel === 'teach') {
      return '/';
    }
  }
  return '/teach';
}

/** In-app logout route for the Teach APT PWA on the current host. */
export function resolveTeachPwaLogoutPath(pathname?: string, hostname?: string): string {
  return isTeachPwaSubdomainHost(
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : ''),
  )
    ? '/logout'
    : '/teach/logout';
}

/**
 * True when the current document is inside the Teach APT PWA shell (apex `/teach` or `teach.*` host).
 */
export function isTeachPwaShellContext(pathname?: string, hostname?: string): boolean {
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/teach');
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  return isTeachPwaSurface(path, host);
}

/** Event detail path for the Teach APT PWA on the current host. */
export function resolveTeachPwaEventPath(
  eventSlug: string,
  pathname?: string,
  hostname?: string,
): string {
  const slug = eventSlug.replace(/^\/+/, '');
  return isTeachPwaSubdomainHost(
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : ''),
  )
    ? `/${slug}`
    : `/teach/${slug}`;
}

/** Event workspace tab path (roster, gradebook, etc.) on the current host. */
export function resolveTeachPwaEventTabPath(
  eventSlug: string,
  tab: string,
  pathname?: string,
  hostname?: string,
): string {
  const base = resolveTeachPwaEventPath(eventSlug, pathname, hostname);
  return `${base}?tab=${encodeURIComponent(tab)}`;
}

/** True when a same-origin path stays inside the Teach APT PWA shell. */
export function isTeachPwaInternalPath(path: string, pathname?: string, hostname?: string): boolean {
  const normalized = path.replace(/\/+$/, '') || '/';
  const currentPath = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/teach');
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');

  if (normalized === '/teach' || normalized.startsWith('/teach/')) {
    return true;
  }
  if (normalized === '/logout-confirmation') {
    return true;
  }
  if (!isTeachPwaSurface(currentPath, host)) {
    return false;
  }
  if (!isTeachPwaSubdomainHost(host)) {
    return false;
  }
  const mainSiteEscapes = new Set([
    '/dashboard',
    '/messages',
    '/notifications',
    '/events',
    '/agencies',
    '/blog',
    '/courses',
    '/tac-shop',
  ]);
  if (mainSiteEscapes.has(normalized) || [...mainSiteEscapes].some((p) => normalized.startsWith(`${p}/`))) {
    return false;
  }
  return true;
}
